import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Plus, LogOut, LayoutDashboard, Menu, BookOpen, GraduationCap } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (to) => location.pathname === to;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const desktopLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm font-medium transition-colors hover:text-amber-400 ${
        isActive(to) ? "text-amber-400" : "text-slate-300"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <nav className="sticky top-0 z-50 w-full glass border-b border-white/10 px-4 lg:px-8 py-3.5 flex items-center justify-between gap-3">
      <Link to="/" data-testid="nav-logo" className="group shrink-0">
        <div className="group-hover:scale-[1.02] transition-transform">
          <Logo size={32} />
        </div>
      </Link>

      {/* Desktop nav */}
      <div className="hidden md:flex items-center gap-5">
        {desktopLink("/campus", "Courses", "nav-campus")}
        {user ? (
          <>
            {desktopLink("/teaching", "My Teaching", "nav-teaching")}
            {user.role === "admin" && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-amber-400 ${
                  isActive("/admin") ? "text-amber-400" : "text-slate-300"
                }`}
              >
                <LayoutDashboard className="h-4 w-4" /> Office
              </Link>
            )}
            <Button
              data-testid="nav-teach"
              onClick={() => navigate("/teach")}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full h-9 px-4"
            >
              <Plus className="h-4 w-4 mr-1" /> Teach
            </Button>
            <button data-testid="nav-logout" onClick={handleLogout} className="text-slate-400 hover:text-white">
              <LogOut className="h-4 w-4" />
            </button>
            {user.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/10" />
            ) : (
              <Link
                to="/account"
                data-testid="nav-account"
                className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-amber-400 hover:ring-2 hover:ring-amber-500/50"
              >
                {user.name?.[0]?.toUpperCase() || "U"}
              </Link>
            )}
          </>
        ) : (
          <>
            {desktopLink("/login", "Sign in", "nav-login")}
            <Button
              data-testid="nav-get-started"
              onClick={() => navigate("/register")}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-9 px-4"
            >
              Get Started
            </Button>
          </>
        )}
      </div>

      {/* Mobile nav */}
      <div className="flex md:hidden items-center gap-2 shrink-0">
        {user && (
          <Button
            data-testid="nav-teach-mobile"
            onClick={() => navigate("/teach")}
            aria-label="Teach a course"
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-full h-9 w-9 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="nav-menu-btn"
              aria-label="Open menu"
              className="h-9 w-9 flex items-center justify-center rounded-lg border border-white/10 text-slate-200 hover:bg-white/5"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#0B0E14] border-white/10 text-white w-[82%] max-w-xs p-0">
            <div className="p-6 border-b border-white/10">
              <Logo size={30} />
            </div>
            <div className="flex flex-col p-4 gap-1">
              {user && (
                <div className="flex items-center gap-3 px-3 py-3 mb-2 rounded-lg bg-white/5">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-9 w-9 rounded-full" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold text-amber-400">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{user.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user.email}</div>
                  </div>
                </div>
              )}
              <MobileItem onClick={() => go("/campus")} testid="mnav-campus" active={isActive("/campus")} icon={BookOpen}>
                Courses
              </MobileItem>
              {user ? (
                <>
                  <MobileItem onClick={() => go("/teaching")} testid="mnav-teaching" active={isActive("/teaching")} icon={GraduationCap}>
                    My Teaching
                  </MobileItem>
                  <MobileItem onClick={() => go("/account")} testid="mnav-account" active={isActive("/account")}>
                    Account
                  </MobileItem>
                  {user.role === "admin" && (
                    <MobileItem onClick={() => go("/admin")} testid="mnav-admin" active={isActive("/admin")} icon={LayoutDashboard}>
                      Manager's Office
                    </MobileItem>
                  )}
                  <Button
                    data-testid="mnav-teach"
                    onClick={() => go("/teach")}
                    className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-full h-11 w-full"
                  >
                    <Plus className="h-4 w-4 mr-1.5" /> Teach a course
                  </Button>
                  <button
                    data-testid="mnav-logout"
                    onClick={handleLogout}
                    className="mt-2 flex items-center gap-2 px-3 h-11 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 text-sm"
                  >
                    <LogOut className="h-4 w-4" /> Log out
                  </button>
                </>
              ) : (
                <>
                  <MobileItem onClick={() => go("/login")} testid="mnav-login" active={isActive("/login")}>
                    Sign in
                  </MobileItem>
                  <Button
                    data-testid="mnav-get-started"
                    onClick={() => go("/register")}
                    className="mt-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-11 w-full"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

const MobileItem = ({ onClick, testid, active, icon: Icon, children }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-2.5 px-3 h-12 rounded-lg text-base font-medium transition-colors ${
      active ? "bg-amber-500/10 text-amber-400" : "text-slate-200 hover:bg-white/5"
    }`}
  >
    {Icon && <Icon className="h-4 w-4" />}
    {children}
  </button>
);
